import styles from './MenuItem.module.scss';

import {
  MouseEvent,
  Ref,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { MenuContext } from './MenuContext';
import { useNavigate } from 'react-router-dom';
import { MenuActionsContext } from './MenuActionsContext';

interface Props {
  children: string;
  onClick?: () => void;
  onAnchorClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  disabled?: boolean;
  color?: 'primary' | 'default' | 'positive' | 'negative';
  href?: string;
}

export function MenuItem({ color = 'default', ...props }: Props) {
  const { disabled = false } = props;
  const id = useId();
  const context = useContext(MenuContext);
  const actions = useContext(MenuActionsContext);
  const [focused, setFocused] = useState<boolean>(false);
  const ref = useRef<HTMLAnchorElement | HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    context.set(id, { ref, disabled, focused });
  }, [context, id, disabled, focused]);

  const onAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (props.onAnchorClick) {
      props.onAnchorClick(event);
    } else if (props.href) {
      navigate(props.href);
    }
  };

  const onBlur = () => {
    setFocused(false);
  };
  const onFocus = () => {
    setFocused(true);
  };

  const className = [styles['menu-item'], styles[`menu-item-color-${color}`]]
    .filter(Boolean)
    .join(' ');

  const onClick = () => {
    props.onClick?.();
    actions.close();
  };

  return props.href ? (
    <a
      ref={ref as Ref<HTMLAnchorElement>}
      className={className}
      href={props.href}
      onBlur={onBlur}
      onClick={onAnchorClick}
      onFocus={onFocus}
      role="menuitem"
    >
      {props.children}
    </a>
  ) : (
    <div
      ref={ref as Ref<HTMLDivElement>}
      className={className}
      onBlur={onBlur}
      onClick={onClick}
      onFocus={onFocus}
      role="menuitem"
      tabIndex={0}
    >
      {props.children}
    </div>
  );
}
